import { z } from 'zod';

// ---------------------------------------------------------------------------
// Lead form
// ---------------------------------------------------------------------------

/**
 * Lead-form field schema for the chat-widget.
 *
 * DIVERGENCE NOTE — compared to formFieldPropsSchema in
 * sites-platform/components/schemas/formfield.ts (separate npm workspace):
 *
 * 1. Identity key: `key` here vs `name` in sites-platform.
 *    INTENTIONAL — the widget API was designed around `key` from day one; the
 *    sites-platform schema uses `name` to match the DOM `name` attribute. Both
 *    serve as field identity; they are domain-layer aliases, not a bug.
 *
 * 2. Extra kind: `checkbox` present here, absent in sites-platform.
 *    INTENTIONAL — the lead-capture form needs consent checkboxes (e.g. "I
 *    agree to be contacted"). The sites-platform schema serves the contact-form
 *    and booking widgets, which have no checkbox requirement.
 *
 * 3. Missing kind: `textarea` absent here, present in sites-platform.
 *    INTENTIONAL — the lead-capture form is a compact pre-chat widget; multi-
 *    line textarea fields are not offered. The sites-platform contact-form does
 *    support textarea.
 *
 * 4. Nullish optionals: `.nullish()` on `required`, `placeholder`, `options`.
 *    INTENTIONAL — the backend may emit JSON `null` for fields that were stored
 *    before these columns were added (legacy rows). `.nullish()` accepts both
 *    `null` and `undefined` and treats them as absent, which is the correct
 *    runtime behavior. The sites-platform schema uses `.optional()` because its
 *    fields are always freshly constructed and never carry null from storage.
 *
 * SYNC: if the type enum or field shape changes here, review formfield.ts in
 * sites-platform/components/schemas/ and update both sides together.
 */
export const LeadFormFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['text', 'email', 'tel', 'select', 'checkbox']),
  // .nullish() instead of .optional() — the backend may emit null for these
  // optional-undefined fields in legacy stored rows; null must be accepted and
  // treated as absent (undefined) by the widget renderer.
  required: z.boolean().nullish(),
  placeholder: z.string().nullish(),
  options: z.array(z.string()).nullish(),
});

export type LeadFormField = z.infer<typeof LeadFormFieldSchema>;

export const LeadCaptureConfigSchema = z.object({
  enabled: z.boolean(),
  title: z.string().optional(),
  fields: z.array(LeadFormFieldSchema),
  submit_label: z.string().optional(),
  privacy_text: z.string().optional(),
});

export type LeadCaptureConfig = z.infer<typeof LeadCaptureConfigSchema>;

// ---------------------------------------------------------------------------
// Widget config — returned by GET /v1/sites/{siteId}/widget/config
// ---------------------------------------------------------------------------

export const WidgetConfigSchema = z.object({
  name: z.string(),
  logo_url: z.string(),
  message_avatar_url: z.string().nullable().optional(),
  accent_color: z.string(),
  position: z.string(),
  welcome_message: z.string(),
  suggested_questions: z.array(z.string()),
  template: z.string(),
  turnstile_site_key: z.string().nullable().optional(),
  turnstile_max_skips: z.number().optional(),
  lead_capture: LeadCaptureConfigSchema.nullable().optional(),
});

export type WidgetConfig = z.infer<typeof WidgetConfigSchema>;

/**
 * Minimal schema used as the first parse step for the config endpoint.
 * The backend may return `{"enabled": false}` with no other fields when the
 * chatbot is disabled.  Parsing with this schema (passthrough) before checking
 * `enabled` prevents the full schema from throwing on the disabled-site case.
 */
export const MinimalConfigResponseSchema = z.object({ enabled: z.boolean() }).passthrough();

export type MinimalConfigResponse = z.infer<typeof MinimalConfigResponseSchema>;

/**
 * Full response from GET /v1/sites/{siteId}/widget/config when `enabled:true`.
 * `session_token` and `name` are required — the backend guarantees them when
 * the chatbot is enabled.  Only parse with this schema AFTER confirming enabled.
 */
export const ConfigResponseSchema = z.object({
  enabled: z.literal(true),
  session_token: z.string(),
  name: z.string(),
  logo_url: z.string().optional().default(''),
  message_avatar_url: z.string().nullable().optional(),
  accent_color: z.string().optional().default('#6366f1'),
  position: z.string().optional().default('bottom-right'),
  welcome_message: z.string().optional().default('Hi! How can I help you today?'),
  suggested_questions: z.array(z.string()).optional().default([]),
  template: z.string().optional().default('classic-bubble'),
  turnstile_site_key: z.string().nullable().optional(),
  turnstile_max_skips: z.number().optional(),
  lead_capture: LeadCaptureConfigSchema.nullable().optional(),
});

export type ConfigResponse = z.infer<typeof ConfigResponseSchema>;

// ---------------------------------------------------------------------------
// Chat messages
// ---------------------------------------------------------------------------

export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  ts: string;
}

export type MessageEntry =
  | { kind: 'message'; data: ChatMessage };

// ---------------------------------------------------------------------------
// Poll endpoint — GET /v1/sites/{siteId}/conversations/{conversationId}/messages?after=<messageId>
// ---------------------------------------------------------------------------

/** Controller ownership — who handles the next visitor message. */
export type ControllerKind = 'ai' | 'merchant';

export const PollMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  created_at: z.number(),
});

export type PollMessage = z.infer<typeof PollMessageSchema>;

export const PollResponseSchema = z.object({
  controller: z.enum(['ai', 'merchant']),
  messages: z.array(PollMessageSchema),
});

export type PollResponse = z.infer<typeof PollResponseSchema>;

// ---------------------------------------------------------------------------
// Conversation persistence
// ---------------------------------------------------------------------------

export interface StoredConversation {
  id: string;
  expires_at: string;
  messages: MessageEntry[];
}

export interface ConversationStore {
  load: (siteId: string) => StoredConversation | null;
  save: (siteId: string, convId: string, messages: MessageEntry[]) => void;
  clear: (siteId: string) => void;
}

// ---------------------------------------------------------------------------
// SSE stream callbacks
// ---------------------------------------------------------------------------

export interface ChatStreamCallbacks {
  onConversationId: (id: string) => void;
  onToken: (token: string) => void;
  onMessageId: (id: string) => void;
  onHandoff: () => void;
  onDone: () => void;
  onError: (error: string) => void;
  onTurnstileRequired?: () => void;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export interface WidgetApiClient {
  fetchConfig: (siteId: string) => Promise<{ config: WidgetConfig; token: string }>;
  sendMessage: (
    siteId: string,
    token: string,
    message: string,
    conversationId: string | null,
    pageUrl: string,
    turnstileToken: string | null,
    callbacks: ChatStreamCallbacks,
  ) => AbortController;
  pollMessages: (
    siteId: string,
    token: string,
    conversationId: string,
    afterMessageId: string | null,
  ) => Promise<PollResponse>;
  submitLead: (
    siteId: string,
    token: string,
    formData: Record<string, string | boolean>,
    pageUrl: string,
  ) => Promise<{ lead_id: string; conversation_id?: string }>;
}
